import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import StatCard from '../../components/common/StatCard';
import DonutChart from '../../components/common/DonutChart';
import Table from '../../components/common/Table';
import Badge from '../../components/common/Badge';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { MapPin, CalendarClock, CheckCircle } from 'lucide-react';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      if (!user) return;
      try {
        const response = await api(`/dashboard/stats`);
        if (response.ok) {
          const stats = await response.json();
          setData(stats);
        }
      } catch (err) {
        console.error("Dashboard error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [user]);

  if (loading) return <MainLayout title="Dashboard"><div style={{ padding: '40px', textAlign: 'center' }}>Loading Dashboard...</div></MainLayout>;
  if (!data) return <MainLayout title="Dashboard"><div style={{ padding: '40px', textAlign: 'center' }}>Error loading data</div></MainLayout>;

  const donutData = data.statusBreakdown.map(sb => ({
    name: sb._id || 'New',
    value: sb.count
  }));

  // Filter followups to those that are site visits if possible, otherwise use today's followups
  const upcomingVisits = data.todayFollowups || [];

  return (
    <MainLayout title="Dashboard">
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--color-text-primary)', marginBottom: '4px' }}>
            Good morning, {user.name.split(' ')[0]} 👋
          </h1>
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '14px' }}>
            Your site visits today for {new Date().toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '24px' }}>
        <StatCard 
          label="Total Assigned Leads" 
          value={data.stats.totalLeads} 
          icon={MapPin}
          gradient="var(--gradient-blue)"
        />
        <StatCard 
          label="Today's Visits" 
          value={upcomingVisits.length} 
          icon={CalendarClock}
          iconColor="var(--chart-orange)"
          color="var(--chart-orange)"
          gradient="var(--gradient-orange)"
        />
        <StatCard 
          label="Completed Visits" 
          value={data.stats.siteVisits} 
          icon={CheckCircle}
          iconColor="var(--chart-green)"
          color="var(--chart-green)"
          gradient="var(--gradient-green)"
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '24px', marginBottom: '24px' }}>
        <div className="card" style={{ 
          background: 'var(--color-background-primary)', 
          padding: '24px', 
          borderRadius: 'var(--border-radius-xl)',
          border: '1px solid var(--color-border-tertiary)',
          boxShadow: 'var(--shadow-sm)'
        }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '20px' }}>Lead Status (My Assigned)</h3>
          <DonutChart data={donutData} />
        </div>
        
        <div className="card" style={{ 
          background: 'var(--color-background-primary)', 
          padding: '24px', 
          borderRadius: 'var(--border-radius-xl)',
          border: '1px solid var(--color-border-tertiary)',
          boxShadow: 'var(--shadow-sm)',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600' }}>Upcoming Visits</h3>
            <button 
              onClick={() => navigate(`/site-visit/leads`)}
              style={{ 
                padding: '6px 12px', 
                borderRadius: 'var(--border-radius-md)', 
                border: '1px solid var(--color-border-primary)',
                background: 'var(--color-background-secondary)',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-border-tertiary)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'var(--color-background-secondary)'}
            >
              View all leads
            </button>
          </div>

          {upcomingVisits.length > 0 ? (
            <div style={{ flex: 1, overflowX: 'auto' }}>
              <Table 
                headers={['Lead', 'Location', 'Due', 'Status']}
                data={upcomingVisits}
                renderRow={(item) => (
                  <>
                    <td 
                      style={{ padding: '14px 16px', fontWeight: '600', cursor: 'pointer', color: 'var(--color-brand-primary)' }}
                      onClick={() => navigate(`/lead/${item._id}`)}
                    >
                      {item.name}
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', color: 'var(--color-text-secondary)' }}>{item.location || 'N/A'}</td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', fontWeight: '500' }}>{item.due}</td>
                    <td style={{ padding: '14px 16px' }}>
                      <Badge type={item.statusType}>{item.status}</Badge>
                    </td>
                  </>
                )}
              />
            </div>
          ) : (
            <div style={{ 
              padding: '40px', 
              textAlign: 'center', 
              color: 'var(--color-text-tertiary)', 
              fontSize: '14px',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              background: 'var(--color-background-secondary)',
              borderRadius: 'var(--border-radius-lg)',
              border: '1px dashed var(--color-border-secondary)'
            }}>
              <div style={{ fontSize: '32px' }}>📍</div>
              <div>No site visits scheduled for today.</div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default Dashboard;
